import { Sidebar } from "./sidebar";
import { Menu, LayoutDashboard, Mic, PenTool, Wand2, Wrench, Archive, Plug, Settings, Mail, SlidersHorizontal, Sparkles } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/store";
import { ScribPostLogo } from "@/components/scribpost-logo";
import { VerificationBanner } from "@/components/verification-banner";

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    const mainContent = document.getElementById("main-content");
    if (mainContent) {
      mainContent.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function AnimatedBackground() {
  return (
    <>
      <div className="bg-orb-1" aria-hidden="true" />
      <div className="bg-orb-2" aria-hidden="true" />
      <div className="bg-orb-3" aria-hidden="true" />
      <div className="bg-grid"  aria-hidden="true" />
      <div className="bg-aurora" aria-hidden="true" />
    </>
  );
}

const MOBILE_SHEET_NAV = [
  { href: "/",             label: "Dashboard",      icon: LayoutDashboard },
  { href: "/generator",    label: "Post Generator",  icon: PenTool         },
  { href: "/studio",       label: "Studio",          icon: Wand2           },
  { href: "/reply",        label: "Smart Message",   icon: Mail            },
  { href: "/training",     label: "Voice & Text",    icon: Mic             },
  { href: "/voice-style",  label: "Voice Style",     icon: SlidersHorizontal },
  { href: "/tools",        label: "Creator Tools",   icon: Wrench          },
  { href: "/vault",        label: "Idea Vault",      icon: Archive         },
  { href: "/integrations", label: "Integrations",    icon: Plug            },
  { href: "/settings",     label: "Settings",        icon: Settings        },
];

const MOBILE_BOTTOM_NAV = [
  { href: "/",         label: "Home",     icon: LayoutDashboard },
  { href: "/studio",   label: "Studio",   icon: Wand2           },
  { href: "/training", label: "Training", icon: Mic             },
  { href: "/vault",    label: "Vault",    icon: Archive         },
  { href: "/settings", label: "Settings", icon: Settings        },
];

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [location] = useLocation();
  const { displayName, isSubscribed } = useAppStore();
  const spotlightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      document.documentElement.style.setProperty("--cx", e.clientX + "px");
      document.documentElement.style.setProperty("--cy", e.clientY + "px");
    };
    window.addEventListener("mousemove", handleMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMove);
  }, []);

  const initial = displayName ? displayName.charAt(0).toUpperCase() : null;
  const greeting = displayName || null;

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <ScrollToTop />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg focus:text-sm focus:font-semibold"
      >
        Skip to main content
      </a>
      <AnimatedBackground />
      <div ref={spotlightRef} className="cursor-spotlight" aria-hidden="true" />

      <Sidebar />

      <main className="flex-1 flex flex-col min-w-0 relative z-10">
        {/* Mobile Header */}
        <header className="md:hidden h-14 border-b border-border/50 bg-card/40 backdrop-blur-xl flex items-center justify-between px-4 sticky top-0 z-50">
          <ScribPostLogo size="sm" />

          <div className="flex items-center gap-2">
            {!isSubscribed && (
              <Link
                href="/pricing"
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold text-primary-foreground transition-all hover:brightness-110"
                style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
                aria-label="Upgrade to Pro"
              >
                <Sparkles className="w-3 h-3" />
                Pro
              </Link>
            )}
            {initial && (
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-primary-foreground text-xs font-bold shadow-md shadow-primary/30">
                {initial}
                {isSubscribed && (
                  <span className="sr-only">Pro member</span>
                )}
              </div>
            )}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <button className="p-2 -mr-1.5 text-muted-foreground hover:text-foreground transition-colors" aria-label="Open navigation menu">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 border-border/50" style={{ background: "hsl(222 44% 7% / 0.95)", backdropFilter: "blur(24px)" }}>
                {/* Profile section */}
                <div className="p-5 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-primary-foreground font-bold text-sm shrink-0 shadow-lg shadow-primary/25">
                      {initial ?? "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">Your profile</p>
                      <p className="font-semibold text-sm truncate">
                        {greeting ? `Hey, ${greeting} 👋` : "Hey, You 👋"}
                        {isSubscribed && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold text-primary-foreground" style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}>
                            PRO
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <nav className="p-3 space-y-0.5">
                  {MOBILE_SHEET_NAV.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setIsOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 text-sm",
                        location === item.href
                          ? "bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-lg shadow-primary/25 sidebar-active-glow"
                          : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                      )}
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      {item.label}
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </header>

        <div
          id="main-content"
          className="flex-1 p-4 md:p-8 lg:p-10 max-w-6xl mx-auto w-full mobile-scroll-padding"
        >
          <VerificationBanner />
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav
        aria-label="Mobile navigation"
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-border/50 flex items-center"
        style={{
          background: "hsl(222 44% 6% / 0.92)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {MOBILE_BOTTOM_NAV.map((item) => {
          const isActive = location === item.href;
          return (
            <Link
              key={item.href}
              href={item.href}
              className="flex-1 flex flex-col items-center justify-center py-2.5 gap-1 min-w-0 relative transition-colors"
            >
              {isActive && (
                <span
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full"
                  style={{ background: "linear-gradient(90deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
                />
              )}
              <item.icon
                className={cn(
                  "w-5 h-5 transition-colors",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}
              />
              <span
                className={cn(
                  "text-[10px] font-medium transition-colors truncate",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}

        {/* Extra "More" button to open the sheet for less-common pages */}
        <button
          onClick={() => setIsOpen(true)}
          className="flex-1 flex flex-col items-center justify-center py-2.5 gap-1 min-w-0 text-muted-foreground"
          aria-label="More navigation options"
        >
          <Menu className="w-5 h-5" />
          <span className="text-[10px] font-medium">More</span>
        </button>
      </nav>
    </div>
  );
}
